/**
 * Privacy Policy Page
 *
 * Displays the full Privacy Policy content fetched from Firestore.
 * Features:
 * - Fetches current privacy version from Firestore
 * - Renders markdown/text content
 * - Shows last updated date
 * - Print button for offline copy
 * - Error handling with retry
 */

import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { getTermsContent, getCurrentTermsConfig } from '../services/termsService';

// Default privacy content (fallback if Firestore unavailable)
const DEFAULT_PRIVACY = {
  title: 'Privacy Policy',
  version: '1.0.0',
  effectiveDate: new Date(),
  content: `
# QuickMeet Privacy Policy

**Last Updated: December 2025**

## 1. Introduction

QuickMeet ("we," "our," or "us") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our video conferencing platform.

## 2. Information We Collect

### 2.1 Information You Provide
- **Account Information**: Email address, name, profile picture
- **Payment Information**: Billing details processed through Stripe
- **Meeting Content**: Chat messages, shared files, recordings (if enabled)
- **Contact Information**: Contacts you add to your address book

### 2.2 Automatically Collected Information
- **Device Information**: Browser type, operating system, device identifiers
- **Usage Data**: Features used, meeting duration, connection quality metrics
- **Log Data**: IP address, access times, pages viewed
- **Cookies**: Session and preference cookies

## 3. How We Use Your Information

We use collected information to:
- Provide and maintain the Service
- Process transactions and send related information
- Send administrative messages and updates
- Respond to your comments and questions
- Analyze usage patterns to improve the Service
- Detect and prevent fraud or abuse
- Comply with legal obligations

## 4. Information Sharing

We may share your information:
- **With Service Providers**: Third parties that perform services on our behalf
- **For Legal Reasons**: When required by law or to protect our rights
- **Business Transfers**: In connection with a merger or acquisition
- **With Your Consent**: When you have given us permission

We do NOT sell your personal information.

## 5. Data Security

We implement appropriate security measures including:
- Encryption of data in transit (TLS/SSL)
- Encryption of data at rest
- Regular security audits
- Access controls and authentication
- Secure data centers

## 6. Data Retention

We retain your information for as long as your account is active or as needed to provide services. You may request deletion of your account and associated data at any time.

## 7. Your Rights

Depending on your location, you may have rights to:
- Access your personal data
- Correct inaccurate data
- Delete your data
- Port your data to another service
- Opt-out of marketing communications
- Withdraw consent

## 8. Children's Privacy

QuickMeet is not intended for children under 13. We do not knowingly collect information from children under 13.

## 9. International Transfers

Your information may be transferred to and processed in countries other than your own. We ensure appropriate safeguards are in place.

## 10. Third-Party Services

Our Service may contain links to third-party websites. We are not responsible for their privacy practices.

## 11. Changes to This Policy

We may update this Privacy Policy periodically. We will notify you of material changes via email or in-app notification.

## 12. Contact Us

For privacy-related questions or to exercise your rights, contact us at:

**Email**: privacy@quickmeet.cam
**Address**: [Your Business Address]

## 13. California Privacy Rights (CCPA)

California residents have additional rights including the right to know, delete, and opt-out of the sale of personal information.

## 14. European Privacy Rights (GDPR)

EU residents have rights under GDPR including access, rectification, erasure, restriction, and objection to processing.
  `.trim()
};

export default function Privacy() {
  const navigate = useNavigate();
  const [privacyContent, setPrivacyContent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Load privacy content
  useEffect(() => {
    const loadPrivacy = async () => {
      try {
        setLoading(true);
        setError(null);

        // Get current config
        const config = await getCurrentTermsConfig();

        // Get privacy content for current version
        const content = await getTermsContent(config.privacyVersion, 'privacy');

        if (content) {
          setPrivacyContent(content);
        } else {
          // Use default if not found in Firestore
          setPrivacyContent(DEFAULT_PRIVACY);
        }
      } catch (err) {
        console.error('Error loading privacy policy:', err);
        setError('Unable to load Privacy Policy. Please try again.');
        // Fallback to default
        setPrivacyContent(DEFAULT_PRIVACY);
      } finally {
        setLoading(false);
      }
    };

    loadPrivacy();
  }, []);

  const handlePrint = () => {
    window.print();
  };

  return (
    <div className="min-h-screen bg-white dark:bg-[#1c1c1e]">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-white/80 dark:bg-[#1c1c1e]/80 backdrop-blur-xl border-b border-[#d2d2d7] dark:border-[#2c2c2e]">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link
            to="/"
            className="flex items-center gap-3 text-[#1d1d1f] dark:text-white hover:opacity-70 transition-opacity"
          >
            <div className="w-10 h-10 bg-gradient-to-br from-[#06c] to-[#0055d4] rounded-xl flex items-center justify-center">
              <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
              </svg>
            </div>
            <span className="font-semibold text-lg">QuickMeet</span>
          </Link>

          <div className="flex items-center gap-3">
            <button
              onClick={handlePrint}
              className="p-2 text-[#6e6e73] hover:text-[#1d1d1f] dark:hover:text-white transition-colors"
              title="Print"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
              </svg>
            </button>
            <button
              onClick={() => navigate(-1)}
              className="px-4 py-2 text-sm font-medium text-[#06c] hover:text-[#0055d4] transition-colors"
            >
              Go Back
            </button>
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-6 py-12">
        {loading ? (
          <div className="flex items-center justify-center py-24">
            <div className="w-10 h-10 border-2 border-[#06c]/30 border-t-[#06c] rounded-full animate-spin" />
          </div>
        ) : error && !privacyContent ? (
          <div className="text-center py-24">
            <div className="w-20 h-20 bg-red-100 dark:bg-red-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
              <svg className="w-10 h-10 text-red-600 dark:text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <h2 className="text-xl font-semibold text-[#1d1d1f] dark:text-white mb-2">
              Unable to Load Privacy Policy
            </h2>
            <p className="text-[#6e6e73] dark:text-[#8e8e93] mb-6">{error}</p>
            <button
              onClick={() => window.location.reload()}
              className="px-6 py-3 bg-[#06c] text-white rounded-xl hover:bg-[#0055d4] transition-colors"
            >
              Try Again
            </button>
          </div>
        ) : (
          <>
            {/* Title and Meta */}
            <div className="mb-12 text-center">
              <h1 className="text-4xl font-semibold text-[#1d1d1f] dark:text-white mb-4">
                {privacyContent?.title || 'Privacy Policy'}
              </h1>
              <p className="text-[#6e6e73] dark:text-[#8e8e93]">
                Version {privacyContent?.version || '1.0.0'} - Last updated{' '}
                {privacyContent?.effectiveDate
                  ? new Date(privacyContent.effectiveDate).toLocaleDateString('en-US', {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric'
                    })
                  : 'December 2025'}
              </p>
            </div>

            {/* Privacy Content */}
            <div className="prose prose-lg dark:prose-invert max-w-none">
              <div className="bg-[#f5f5f7] dark:bg-[#2c2c2e] rounded-2xl p-8 whitespace-pre-wrap text-[#1d1d1f] dark:text-[#e5e5ea] leading-relaxed">
                {privacyContent?.content || DEFAULT_PRIVACY.content}
              </div>
            </div>

            {/* Footer Links */}
            <div className="mt-12 pt-8 border-t border-[#d2d2d7] dark:border-[#2c2c2e] flex flex-wrap gap-6 justify-center text-sm">
              <Link to="/terms" className="text-[#06c] hover:underline">
                Terms of Service
              </Link>
              <Link to="/about" className="text-[#06c] hover:underline">
                About QuickMeet
              </Link>
              <Link to="/security" className="text-[#06c] hover:underline">
                Security
              </Link>
              <a href="mailto:privacy@quickmeet.cam" className="text-[#06c] hover:underline">
                Contact Privacy Team
              </a>
            </div>
          </>
        )}
      </main>

      {/* Footer */}
      <footer className="border-t border-[#d2d2d7] dark:border-[#2c2c2e] py-8 mt-12">
        <div className="max-w-4xl mx-auto px-6 text-center text-sm text-[#6e6e73] dark:text-[#8e8e93]">
          <p>&copy; 2025 TheJhyeFactor. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
}
